import React, { useEffect } from 'react';
import { Route, RouteProps, RouteComponentProps, Redirect } from 'react-router-dom';
import { useKennoTenantState } from './context/kennoTenantContext';
import { Routes } from './constants/routes';
import { isLoggedIn } from './utils';
import { useLogout } from './hooks/useLogout';

const LOGOUT_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

export type AuthenticatedRouteComponent = (props: RouteComponentProps & { slug?: string }) => JSX.Element;

interface AuthenticatedRouteProps extends RouteProps {
  component: AuthenticatedRouteComponent;
  slug?: string;
  authenticationRedirectRoute?: string;
}

function logoutOnInactivity(callback: () => void) {
  const now = Date.now();
  const lastActivity = Number.parseInt(window.localStorage.getItem('lastActivity') || now.toString());
  window.localStorage.setItem('lastActivity', now.toString());
  if (lastActivity < now - LOGOUT_TIMEOUT) {
    callback();

    // will clear all the data from the previous session about the user
    window.localStorage.clear();
    window.localStorage.setItem('lastActivity', now.toString());
    return true;
  }
  return false;
}

const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
  component: Component,
  slug,
  authenticationRedirectRoute = Routes.SIGN_IN,
  ...rest
}) => {
  const { user } = useKennoTenantState();
  const { logout } = useLogout();

  useEffect(() => {
    logoutOnInactivity(logout);
  });

  if (!user.isLoaded) {
    return null;
  }

  return (
    <Route
      {...rest}
      render={(routeProps) =>
        isLoggedIn(user) ? (
          <Component {...routeProps} slug={slug} />
        ) : (
          <Redirect
            to={{
              pathname: authenticationRedirectRoute,
              search: window.location.search,
              state: { from: routeProps.location },
            }}
          />
        )
      }
    />
  );
};

export default AuthenticatedRoute;
